import { Box, styled } from '@mui/material';
import Button from 'components/Button';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormValues } from '.';
import { BankOperationActionsMode } from './BankOperationActions';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';

const SubmitContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  alignContent: 'center',
  height: '100%',

  [theme.breakpoints.down('md')]: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    paddingBottom: theme.spacing(6),
  },
}));

type Props = {
  vehicleId: string;
  bankAccountId: string;
  bankAccountOperationId: string;
  bankOperationAmountTcy: number;
  matchingReconcilableTransactions: Array<{
    id: string;
    counterpartyLabel: string;
    actionRef: ActionRef;
    currencyCode: CurrencyCode;
    operationType: ActionOperationType;
  }>;
};

const TransactionActionSubmit: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  bankAccountOperationId,
  bankOperationAmountTcy,
  matchingReconcilableTransactions,
}) => {
  const { handleSubmit: onSubmit, watch } = useFormContext<FormValues>();
  const mode = watch('mode');

  const reconcileOperation =
    api.bankAccount.reconcileBankAccountOperation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Transaction reconciled.',
      }),
    );

  const createTransfer = api.bankAccount.createTransfer.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Transfer created.',
    }),
  );

  const handleSubmit = (values: FormValues) => {
    if (!bankOperationAmountTcy) return;

    if (values.mode === BankOperationActionsMode.QuickReconcile) {
      const transaction = matchingReconcilableTransactions.find(
        (transaction) => transaction.id === values.transactionId,
      );

      if (!transaction) return;

      reconcileOperation.mutate({
        vehicleId,
        bankAccountId,
        bankAccountOperationId,
        reconciledTransactions: [
          {
            id: values.transactionId,
            selectedAmount: bankOperationAmountTcy,
            operationType: transaction.operationType,
            currencyCode: transaction.currencyCode,
            actionRef: transaction.actionRef,
          },
        ],
      });
    } else if (values.mode === BankOperationActionsMode.CreateTransfer) {
      createTransfer.mutate({
        vehicleId,
        sourceAccountId: bankAccountId,
        destinationAccountId: values.targetBankAccountId,
        sourceBankOperationId: bankAccountOperationId,
        reference: values.reference,
        transactions: values.transactions,
        currencyRate: values.currencyRate,
      });
    }
  };

  const visible =
    !!matchingReconcilableTransactions.length ||
    mode === BankOperationActionsMode.CreateTransfer;

  return (
    <SubmitContainer>
      {visible && (
        <Button
          onClick={onSubmit(handleSubmit)}
          variant="outlined"
          size="large"
          loading={reconcileOperation.isPending || createTransfer.isPending}
        >
          OK
        </Button>
      )}
    </SubmitContainer>
  );
};

export default TransactionActionSubmit;
